import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const ReviewsSection = () => {
  const { reviews: reviewsData } = useSiteData();
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section id="reviews" ref={ref} className="py-24 bg-gradient-to-br from-blue-50 via-white to-orange-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            पाठकों की समीक्षाएं
          </h2>
          <p 
            className="text-xl text-orange-600 font-semibold"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            प्रबुद्ध पाठकों के विचार
          </p>
          <p className="text-gray-600 mt-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>पुस्तक के विषय में पाठक क्या कहते हैं</p>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {reviewsData.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="group relative"
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 h-full">
                {/* Quote Icon */}
                <motion.div
                  className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <Quote className="h-6 w-6 text-white" />
                </motion.div>

                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-orange-500 fill-current" />
                  ))}
                </div>

                {/* Review Text */}
                <p 
                  className="text-gray-700 text-base leading-relaxed mb-6 italic"
                  style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                >
                  "{review.review}"
                </p>

                {/* Reviewer Info */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                  <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-orange-400">
                    <img 
                      src={review.image}
                      alt={review.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 
                      className="font-bold text-blue-900"
                      style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    >
                      {review.name}
                    </h4>
                    <p 
                      className="text-sm text-gray-600"
                      style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    >
                      {review.designation}
                    </p>
                  </div>
                </div>

                {/* Hover Effect Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 shadow-2xl">
            <div className="grid md:grid-cols-3 gap-8 text-white">
              <div>
                <div className="text-4xl font-bold mb-2">50,000+</div>
                <div className="text-orange-100" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>प्रसन्न पाठक</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">4.8/5</div>
                <div className="text-orange-100" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>औसत रेटिंग</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">1,200+</div>
                <div className="text-orange-100" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>समीक्षाएं</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewsSection;